import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { START_OUTBOUND_CALL } from '../../graphql/operations';
import './OutboundCallComposer.css';

function PhonePlusIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.6 17.6 0 0 0 4.169 6.605 17.6 17.6 0 0 0 6.605 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.75 1.75 0 0 1-1.657-.459L5.482 8.062a1.75 1.75 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328z" />
      <circle cx="12.5" cy="3.5" r="2.5" fill="currentColor" />
      <path d="M12 2v3M10.5 3.5h3" stroke="white" strokeWidth="0.8" strokeLinecap="round" />
    </svg>
  );
}

const E164_PATTERN = /^\+[1-9]\d{7,14}$/;
const NOTE_MAX_LENGTH = 200;

function normalizePhoneInput(value) {
  if (!value) return '';
  const trimmed = String(value).trim();
  return trimmed.replace(/[^+\d]/g, '');
}

function formatPhoneForDisplay(value) {
  const digits = String(value).replace(/[^0-9]/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  return value;
}

export default function OutboundCallComposer({ onCallQueued }) {
  const [expanded, setExpanded] = useState(false);
  const [toNumber, setToNumber] = useState('');
  const [note, setNote] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [mutationError, setMutationError] = useState(null);

  const triggerRef = useRef(null);
  const firstInputRef = useRef(null);
  const popoverRef = useRef(null);

  const [startOutboundCall, { loading: submitting }] = useMutation(START_OUTBOUND_CALL);

  // Focus management: first input on expand, trigger on collapse.
  useEffect(() => {
    if (expanded && firstInputRef.current) {
      firstInputRef.current.focus();
    } else if (!expanded && triggerRef.current && document.activeElement?.closest('.outbound-call-composer-popover')) {
      triggerRef.current.focus();
    }
  }, [expanded]);

  // Click-outside to dismiss; ESC closes when not in confirm step (so a stray
  // ESC during typing doesn't lose the in-progress form).
  const handleOutsideClick = useCallback((event) => {
    if (!expanded) return;
    const target = event.target;
    if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) {
      return;
    }
    setExpanded(false);
    setConfirming(false);
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return undefined;
    document.addEventListener('mousedown', handleOutsideClick);
    function handleEsc(event) {
      if (event.key === 'Escape' && !confirming) {
        setExpanded(false);
      }
    }
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [expanded, confirming, handleOutsideClick]);

  function reset() {
    setToNumber('');
    setNote('');
    setPhoneError('');
    setConfirming(false);
    setMutationError(null);
  }

  function handleToggle() {
    if (expanded) {
      reset();
      setExpanded(false);
    } else {
      setExpanded(true);
    }
  }

  function handleNumberChange(event) {
    const next = normalizePhoneInput(event.target.value);
    setToNumber(next);
    if (phoneError) setPhoneError('');
    if (confirming) setConfirming(false);
  }

  function handleNoteChange(event) {
    setNote(event.target.value.slice(0, NOTE_MAX_LENGTH));
    if (confirming) setConfirming(false);
  }

  function validateAndStartConfirm(event) {
    event.preventDefault();
    setMutationError(null);
    const trimmed = normalizePhoneInput(toNumber);
    if (!E164_PATTERN.test(trimmed)) {
      setPhoneError('Enter a valid E.164 number, e.g. +15551234567');
      return;
    }
    setPhoneError('');
    setConfirming(true);
  }

  async function handleConfirm() {
    setMutationError(null);
    try {
      const input = { toNumber: normalizePhoneInput(toNumber) };
      const trimmedNote = note.trim();
      if (trimmedNote) input.note = trimmedNote;
      await startOutboundCall({ variables: { input } });
      reset();
      setExpanded(false);
      if (onCallQueued) onCallQueued();
    } catch (err) {
      // KAP-291: surface OUTBOUND_PILOT_BLOCKED with a clearer hint so an
      // operator knows to ask an admin to widen the pilot allowlist rather
      // than thinking the dial failed for a generic reason. The error code
      // is propagated via the GraphQL response either on err.code (Apollo
      // strips this onto err.graphQLErrors[0].extensions.code, but many
      // codepaths put it on the top-level message) or embedded in
      // err.message itself.
      const message = String(err?.message || '');
      const code = err?.graphQLErrors?.[0]?.extensions?.code || err?.code || '';
      if (code === 'OUTBOUND_PILOT_BLOCKED' || /OUTBOUND_PILOT_BLOCKED|not in the pilot allowlist/i.test(message)) {
        setMutationError('Destination is not in the pilot allowlist. Ask an admin to add the number to VOICE_PILOT_ALLOWLIST_OUTBOUND_TO before retrying.');
      } else {
        setMutationError(message || 'Failed to queue outbound call.');
      }
      setConfirming(false);
    }
  }

  function handleCancelConfirm() {
    setConfirming(false);
  }

  return (
    <div className="outbound-call-composer">
      <button
        ref={triggerRef}
        type="button"
        className={`outbound-call-composer-trigger ${expanded ? 'expanded' : ''}`}
        onClick={handleToggle}
        aria-expanded={expanded}
        aria-controls="outbound-call-composer-form"
      >
        <PhonePlusIcon />
        <span className="outbound-call-composer-trigger-label">Place call</span>
      </button>

      {expanded && (
        <form
          ref={popoverRef}
          id="outbound-call-composer-form"
          className="outbound-call-composer-popover"
          onSubmit={validateAndStartConfirm}
          aria-busy={submitting}
          role="dialog"
          aria-label="Place outbound call"
        >
          <div className="outbound-call-composer-field">
            <label htmlFor="outbound-to-number">To number</label>
            <input
              ref={firstInputRef}
              id="outbound-to-number"
              type="tel"
              inputMode="tel"
              autoComplete="off"
              placeholder="+15551234567"
              value={toNumber}
              onChange={handleNumberChange}
              disabled={submitting}
              aria-invalid={Boolean(phoneError) || undefined}
              aria-describedby={phoneError ? 'outbound-to-number-error' : undefined}
              required
            />
            {phoneError && (
              <p id="outbound-to-number-error" className="outbound-call-composer-error" role="alert">
                {phoneError}
              </p>
            )}
          </div>

          <div className="outbound-call-composer-field">
            <label htmlFor="outbound-note">
              Note <span className="outbound-call-composer-optional">(optional)</span>
            </label>
            <textarea
              id="outbound-note"
              rows={2}
              maxLength={NOTE_MAX_LENGTH}
              placeholder="Reason for call"
              value={note}
              onChange={handleNoteChange}
              disabled={submitting}
              aria-describedby="outbound-note-counter"
            />
            <p id="outbound-note-counter" className="outbound-call-composer-counter">
              {note.length}/{NOTE_MAX_LENGTH}
            </p>
          </div>

          {mutationError && (
            <div className="outbound-call-composer-mutation-error" role="alert">
              {mutationError}
            </div>
          )}

          {confirming ? (
            <div className="outbound-call-composer-confirm" role="group" aria-label="Confirm outbound call">
              <p className="outbound-call-composer-confirm-text">
                Place call to <strong>{formatPhoneForDisplay(toNumber)}</strong>?
              </p>
              <div className="outbound-call-composer-confirm-actions">
                <button
                  type="button"
                  className="outbound-call-composer-confirm-yes"
                  onClick={handleConfirm}
                  disabled={submitting}
                >
                  {submitting ? 'Queuing…' : 'Confirm'}
                </button>
                <button
                  type="button"
                  className="outbound-call-composer-confirm-no"
                  onClick={handleCancelConfirm}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="outbound-call-composer-actions">
              <button
                type="submit"
                className="outbound-call-composer-submit"
                disabled={submitting || !toNumber}
              >
                Continue
              </button>
            </div>
          )}

          <p className="outbound-call-composer-hint">
            Queued only — Twilio dial integration ships in a follow-up slice; the call won’t ring out yet but the row will appear in the active list.
          </p>
        </form>
      )}
    </div>
  );
}
